import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { Flex, Select, Text } from "@jsq/ds/core";
import { Form, FormSelect } from "@jsq/ds/forms";
import { CustomReportRecipientObjectType } from "~/graphql/supergraphEnums";
import {
  missingDatesPrompt,
  PreviewDateParameterFields,
  usePreviewDateParameters,
  type DateParameterFieldsState,
} from "~/pages/Reporting/dateParameters";
import { useTemplatePreviewJob } from "~/pages/Reporting/ReportTemplateViewer/hooks/useTemplatePreviewJob";
import { SPACING } from "~/styles/variables";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { useArenaOptions, type ArenaOption } from "../hooks/useArenaOptions";
import { usePreviewEntities } from "../hooks/usePreviewEntities";
import { usePreviewPositions } from "../hooks/usePreviewPositions";
import { usePreviewTransactionBatches } from "../hooks/usePreviewTransactionBatches";
import { useTemplatePreviewUpload } from "../hooks/useTemplatePreviewUpload";
import * as styles from "../styles_vanilla.css";
import {
  isTransactionBasedReport,
  REPORT_TYPE_CONFIG,
  type PreviewEntityInfo,
  type PreviewSelectOption,
  type ReportType,
} from "../types";
import { PreviewPanel, PreviewPlaceholder } from "./PreviewPanel";
import type { TemplateFileData } from "./TemplateCreation";

export type PreviewFormValues = {
  reportType: ReportType;
  /** Single account selection — used by account-statement reports. */
  account: string;
  /** Selected entities. Account-statement reports use a single entry; transaction-based reports allow many. */
  entities: string[];
  /** Selected transaction batch relay IDs — used by transaction-based reports. */
  transactionBatches: string[];
};

export type PreviewPublishStepProps = {
  templateData: TemplateFileData | null;
  arenaId: string;
  arenaName: string;
  initialReportType: string;
  dataPackageName: string;
  /** required to render a draft template*/
  queryId: number | null;
  /** Arena owning the selected data package; null for system packages. Scopes
   * the query lookup, which is independent of the arena being previewed against. */
  dataPackageArenaId: number | null;
  templateName: string;
  isSystemTemplate?: boolean;
  savedFormValues?: PreviewFormValues | null;
  onFormValuesChange?: (values: PreviewFormValues) => void;
};

type PreviewSelectsProps = {
  entities: PreviewEntityInfo[];
  entitiesLoading: boolean;
  accounts: PreviewSelectOption[];
  accountsLoading: boolean;
  isSystemTemplate?: boolean;
  arenas: ArenaOption[];
  arenasLoading: boolean;
  selectedArenaId: number | null;
  onArenaChange: (arenaId: number | null) => void;
  /** Whether to render transaction-batch selects instead of account selects. */
  isTransactionBased: boolean;
  /** Integer arena DB ID — forwarded to the GL transaction data query. */
  arenaDbId: number | null;
  /** Report query ID — forwarded to the GL transaction data query. */
  queryId: number | null;
  dateParameters: DateParameterFieldsState;
};

/** Account-statement selects: single Entity + single Account. */
const AccountStatementSelects = ({
  accounts,
  accountsLoading,
  entities,
  entitiesLoading,
  isSystemTemplate,
  arenas,
  arenasLoading,
  selectedArenaId,
  onArenaChange,
}: Omit<
  PreviewSelectsProps,
  "isTransactionBased" | "arenaDbId" | "queryId" | "dateParameters"
>) => {
  const { values, setFieldValue } = useFormikContext<PreviewFormValues>();

  const selectedEntity = values.entities[0] ?? "";

  useEffect(() => {
    if (!entitiesLoading && entities.length > 0 && values.entities.length === 0) {
      void setFieldValue("entities", [entities[0].id]);
    }
  }, [entities, entitiesLoading, values.entities.length, setFieldValue]);

  const prevEntityRef = useRef("");
  useEffect(() => {
    if (prevEntityRef.current && prevEntityRef.current !== selectedEntity) {
      void setFieldValue("account", "");
    }
    prevEntityRef.current = selectedEntity;
  }, [selectedEntity, setFieldValue]);

  const handleArenaChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      if (raw === undefined || raw === "") {
        onArenaChange(null);
      } else {
        const id = Number(raw);
        onArenaChange(Number.isNaN(id) ? null : id);
      }
      void setFieldValue("entities", []);
      void setFieldValue("account", "");
    },
    [onArenaChange, setFieldValue],
  );

  const handleEntityChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      void setFieldValue("entities", raw === undefined || raw === "" ? [] : [String(raw)]);
    },
    [setFieldValue],
  );

  return (
    <>
      {isSystemTemplate && (
        <Select
          label="Client"
          placeholder="Select client"
          full
          disabled={arenasLoading}
          value={selectedArenaId != null ? String(selectedArenaId) : ""}
          onChange={handleArenaChange}
        >
          <Select.Section>
            {arenas.map((arena) => (
              <Select.Option key={arena.id} value={String(arena.id)}>
                {arena.name}
              </Select.Option>
            ))}
          </Select.Section>
        </Select>
      )}

      <Select
        label="Entity"
        placeholder="Select entity"
        full
        matchFromStart={false}
        disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
        value={selectedEntity}
        onChange={handleEntityChange}
      >
        <Select.Section>
          {entities.map((entity) => (
            <Select.Option key={entity.id} value={entity.id}>
              {entity.name}
            </Select.Option>
          ))}
        </Select.Section>
      </Select>

      <FormSelect
        name="account"
        label="Account"
        placeholder="Select account"
        full
        disabled={accountsLoading || !selectedEntity}
      >
        <Select.Section>
          {accounts.map((account) => (
            <Select.Option key={account.id} value={account.id}>
              {account.name}
            </Select.Option>
          ))}
        </Select.Section>
      </FormSelect>
    </>
  );
};

/** Transaction-based selects: multi-select Entity + multi-select Transactions + recipient Account. */
const TransactionSelects = ({
  accounts,
  accountsLoading,
  entities,
  entitiesLoading,
  isSystemTemplate,
  arenas,
  arenasLoading,
  selectedArenaId,
  onArenaChange,
  arenaDbId,
  queryId,
}: Omit<PreviewSelectsProps, "isTransactionBased" | "dateParameters">) => {
  const { values, setFieldValue } = useFormikContext<PreviewFormValues>();

  const { batches: transactionBatches, loading: batchesLoading } = usePreviewTransactionBatches(
    values.entities,
    arenaDbId,
    queryId,
  );

  const handleArenaChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      if (raw === undefined || raw === "") {
        onArenaChange(null);
      } else {
        const id = Number(raw);
        onArenaChange(Number.isNaN(id) ? null : id);
      }
      void setFieldValue("entities", []);
      void setFieldValue("transactionBatches", []);
      void setFieldValue("account", "");
    },
    [onArenaChange, setFieldValue],
  );

  const handleEntityChange = useCallback(
    (value: string | number | string[] | number[]) => {
      // Clear transaction batch and account selection when entities change:
      // the recipient account list is scoped to the first selected entity.
      void setFieldValue("transactionBatches", []);
      void setFieldValue("account", "");
      void setFieldValue(
        "entities",
        Array.isArray(value) ? (value as string[]) : [String(value)],
      );
    },
    [setFieldValue],
  );

  const hasEntities = values.entities.length > 0;

  return (
    <>
      {isSystemTemplate && (
        <Select
          label="Client"
          placeholder="Select client"
          full
          disabled={arenasLoading}
          value={selectedArenaId != null ? String(selectedArenaId) : ""}
          onChange={handleArenaChange}
        >
          <Select.Section>
            {arenas.map((arena) => (
              <Select.Option key={arena.id} value={String(arena.id)}>
                {arena.name}
              </Select.Option>
            ))}
          </Select.Section>
        </Select>
      )}

      <FormSelect
        name="entities"
        label="Entity"
        placeholder="Select entities"
        full
        multiple
        matchFromStart={false}
        hasLeftPadding={false}
        disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
        handleChange={handleEntityChange}
      >
        <Select.Section>
          {entities.map((entity) => (
            <Select.Option key={entity.id} value={entity.id}>
              {entity.name}
            </Select.Option>
          ))}
        </Select.Section>
      </FormSelect>

      <FormSelect
        name="transactionBatches"
        label="Transactions"
        placeholder="Select transactions"
        full
        multiple
        hasLeftPadding={false}
        disabled={batchesLoading || !hasEntities}
      >
        <Select.Section>
          {transactionBatches.map((batch) => (
            <Select.Option key={batch.id} value={batch.id}>
              {batch.label}
            </Select.Option>
          ))}
        </Select.Section>
      </FormSelect>

      <FormSelect
        name="account"
        label="Account"
        placeholder="Select account"
        full
        disabled={accountsLoading || !hasEntities}
      >
        <Select.Section>
          {accounts.map((account) => (
            <Select.Option key={account.id} value={account.id}>
              {account.name}
            </Select.Option>
          ))}
        </Select.Section>
      </FormSelect>
    </>
  );
};

const PreviewSelects = ({
  accounts,
  accountsLoading,
  entities,
  entitiesLoading,
  isSystemTemplate,
  arenas,
  arenasLoading,
  selectedArenaId,
  onArenaChange,
  isTransactionBased,
  arenaDbId,
  queryId,
  dateParameters,
}: PreviewSelectsProps) => (
  // The date inputs sit outside the report-shape branch: every report type can
  // have a query that asks for dates, and the preview job waits on them either way.
  <Flex flexDirection="column" gap={SPACING.MEDIUM}>
    {isTransactionBased ? (
      <TransactionSelects
        accounts={accounts}
        accountsLoading={accountsLoading}
        entities={entities}
        entitiesLoading={entitiesLoading}
        isSystemTemplate={isSystemTemplate}
        arenas={arenas}
        arenasLoading={arenasLoading}
        selectedArenaId={selectedArenaId}
        onArenaChange={onArenaChange}
        arenaDbId={arenaDbId}
        queryId={queryId}
      />
    ) : (
      <AccountStatementSelects
        accounts={accounts}
        accountsLoading={accountsLoading}
        entities={entities}
        entitiesLoading={entitiesLoading}
        isSystemTemplate={isSystemTemplate}
        arenas={arenas}
        arenasLoading={arenasLoading}
        selectedArenaId={selectedArenaId}
        onArenaChange={onArenaChange}
      />
    )}

    <PreviewDateParameterFields dateParameters={dateParameters} />
  </Flex>
);

type PreviewFormContentProps = {
  templateData: TemplateFileData | null;
  arenaId: string;
  queryId: number | null;
  dataPackageArenaId: number | null;
  templateName: string;
  isSystemTemplate?: boolean;
};

const PreviewFormContent = ({
  templateData,
  arenaId,
  queryId,
  dataPackageArenaId,
  templateName,
  isSystemTemplate,
}: PreviewFormContentProps) => {
  const { values } = useFormikContext<PreviewFormValues>();

  const transactionBased = isTransactionBasedReport(values.reportType);

  const { arenas, arenasLoading, selectedArenaId, setSelectedArenaId } =
    useArenaOptions(!isSystemTemplate);

  const effectiveArenaId = isSystemTemplate
    ? selectedArenaId != null
      ? toGlobalId("ArenaObjectType", selectedArenaId)
      : ""
    : arenaId;

  const { entities, loading: entitiesLoading } = usePreviewEntities(effectiveArenaId);

  // The recipient account list is resolved from the first selected entity:
  // account-statement reports select a single entity; transaction-based reports
  // scope the list to the first of many.
  const entityForPreview = values.entities[0] ?? "";
  const { accounts, loading: accountsLoading } = usePreviewPositions(entityForPreview);

  const effectiveArenaDbId = effectiveArenaId ? toDbId(effectiveArenaId) : null;
  // Both paths: the user explicitly picks the recipient account.
  const recipientAccountDbId = values.account ? toDbId(values.account) : null;

  const hasEntities = values.entities.length > 0;
  const hasTransactions = values.transactionBatches.length > 0;

  // Keep the GL transaction IDs as strings end-to-end so IDs larger than
  // Number.MAX_SAFE_INTEGER survive the round-trip to the preview job.
  const glTransactionIds = transactionBased ? values.transactionBatches.filter(Boolean) : null;

  // For account statements: preview requires an account selection.
  // For transaction-based reports: preview requires at least one entity, one transaction
  // batch, and a recipient account selection.
  const skipPreview = transactionBased
    ? !hasEntities || !hasTransactions || !values.account
    : !values.account;

  const dateParameters = usePreviewDateParameters({
    queryId,
    arenaId: dataPackageArenaId,
  });

  // The draft preview renders through the same async Temporal workflow as the viewer.
  // A failed date-requirements fetch also skips the upload: it can't lead to a
  // render, and PreviewPanel's spinner takes precedence over errors, so an
  // in-flight upload would hide `loadError` until the upload timed out.
  const { templateS3Key, isUploading, uploadError } = useTemplatePreviewUpload({
    templateFile: templateData,
    arenaId: effectiveArenaDbId,
    templateName,
    skip: skipPreview || Boolean(dateParameters.loadError),
  });

  const {
    pdfUrl,
    isRendering: asyncRendering,
    renderError: asyncError,
  } = useTemplatePreviewJob({
    templateS3Key,
    queryId,
    recipientObjectType: CustomReportRecipientObjectType.Account,
    recipientObjectId: recipientAccountDbId,
    arenaGlobalId: effectiveArenaId,
    glTransactionIds,
    parameters: dateParameters.parameters,
    skip: skipPreview || !dateParameters.isResolved || !dateParameters.isComplete,
  });

  const isRendering = isUploading || asyncRendering;
  const renderError = uploadError ?? asyncError;

  const placeholder = useMemo(() => {
    if (!templateData) {
      return (
        <PreviewPlaceholder
          title="No template loaded"
          description="Go back to Step 2 to create or edit a template."
        />
      );
    }
    if (!hasEntities) {
      return (
        <PreviewPlaceholder
          title="Select an entity to preview"
          description="Choose an entity to generate a preview with sample data."
        />
      );
    }
    if (transactionBased && !hasTransactions) {
      return (
        <PreviewPlaceholder
          title="Select transactions to preview"
          description="Choose one or more transactions to generate a preview."
        />
      );
    }
    if (!values.account) {
      return (
        <PreviewPlaceholder
          title="Select an account to preview"
          description="Choose an account to generate a preview with sample data."
        />
      );
    }
    if (dateParameters.isResolved && !dateParameters.isComplete) {
      return <PreviewPlaceholder {...missingDatesPrompt(dateParameters.requiredParameters)} />;
    }
    // Bridges the debounce window before isRendering flips true.
    return (
      <PreviewPlaceholder
        title="Preparing preview…"
        description="Hang tight while we generate a preview with sample data."
      />
    );
  }, [
    templateData,
    hasEntities,
    transactionBased,
    hasTransactions,
    values.account,
    dateParameters.isResolved,
    dateParameters.isComplete,
    dateParameters.requiredParameters,
  ]);

  return (
    <Flex className={styles.previewPublishWrapper}>
      <Flex flexDirection="column" gap={4} className={styles.previewFormSidebar}>
        <Text variant="strong-md">Preview template</Text>
        <Text variant="regular-sm">
          Set report parameters to see a preview. {isSystemTemplate ? "Save" : "Publish"} when
          ready.
        </Text>
        <div className={styles.previewSelectsContainer}>
          <PreviewSelects
            entities={entities}
            entitiesLoading={entitiesLoading}
            accounts={accounts}
            accountsLoading={accountsLoading}
            isSystemTemplate={isSystemTemplate}
            arenas={arenas}
            arenasLoading={arenasLoading}
            selectedArenaId={selectedArenaId}
            onArenaChange={setSelectedArenaId}
            isTransactionBased={transactionBased}
            arenaDbId={effectiveArenaDbId ? Number(effectiveArenaDbId) : null}
            queryId={queryId}
            dateParameters={dateParameters}
          />
        </div>
      </Flex>
      <PreviewPanel
        pdfUrl={pdfUrl}
        isRendering={isRendering}
        error={dateParameters.loadError ?? renderError}
        placeholder={placeholder}
      />
    </Flex>
  );
};

const FormValuesWatcher = ({
  onChange,
}: {
  onChange?: (values: PreviewFormValues) => void;
}) => {
  const { values } = useFormikContext<PreviewFormValues>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current?.(values);
  }, [values]);

  return null;
};

const isValidReportType = (value: string): value is ReportType =>
  REPORT_TYPE_CONFIG.some((config) => config.value === value);

export const PreviewPublishStep = ({
  templateData,
  arenaId,
  initialReportType,
  queryId,
  dataPackageArenaId,
  templateName,
  isSystemTemplate,
  savedFormValues,
  onFormValuesChange,
}: PreviewPublishStepProps) => {
  const initialValues: PreviewFormValues = useMemo(() => {
    if (savedFormValues) {
      // Back-fill new array fields when restoring persisted state that predates them.
      return {
        entities: savedFormValues.entities ?? [],
        transactionBatches: savedFormValues.transactionBatches ?? [],
        account: savedFormValues.account,
        reportType: savedFormValues.reportType,
      };
    }
    return {
      reportType: isValidReportType(initialReportType)
        ? initialReportType
        : REPORT_TYPE_CONFIG[0].value,
      account: "",
      entities: [],
      transactionBatches: [],
    };
  }, [initialReportType, savedFormValues]);

  return (
    <Form<PreviewFormValues> initialValues={initialValues} onSubmit={() => {}}>
      <FormValuesWatcher onChange={onFormValuesChange} />
      <PreviewFormContent
        templateData={templateData}
        arenaId={arenaId}
        queryId={queryId}
        dataPackageArenaId={dataPackageArenaId}
        templateName={templateName}
        isSystemTemplate={isSystemTemplate}
      />
    </Form>
  );
};
